import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router';
import { Briefcase, Check, Clock, Package, Phone, Star, Store, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useAccountStatus } from '@/hooks/queries/useAccountStatus';
import { useSubscription } from '@/hooks/queries/useSubscription';
import { usePlans, type Plan } from '@/hooks/queries/usePlans';
import { formatCurrency } from '@/lib/currency';
import { ownerPath } from '@/lib/hosts';
import { cn } from '@/lib/utils';

const ALLOWED_PATHS = ['/subscription', '/support'];

type Cycle = 'monthly' | 'quarterly' | 'yearly';

function isAllowedPath(pathname: string): boolean {
  return ALLOWED_PATHS.some(
    (path) => pathname.startsWith(path) || pathname.startsWith(ownerPath(path)),
  );
}

function calcPriceForCycle(
  plan: Plan,
  cycle: Cycle,
): { display: number; total: number; saving: number } {
  if (cycle === 'monthly') {
    return { display: plan.price_monthly, total: plan.price_monthly, saving: 0 };
  }
  if (cycle === 'yearly') {
    // Use plan.price_yearly if backend exposes it as full year price; saving 20% by convention
    const monthly = plan.price_yearly > 0 ? plan.price_yearly / 12 : plan.price_monthly * 0.8;
    return {
      display: monthly,
      total: plan.price_yearly || plan.price_monthly * 12 * 0.8,
      saving: 20,
    };
  }
  // quarterly: 10% off, paid 3 months
  const monthly = plan.price_monthly * 0.9;
  return { display: monthly, total: monthly * 3, saving: 10 };
}

export function ReconversionScreen() {
  const { t } = useTranslation('billing');
  const location = useLocation();
  const { user } = useAuth();
  const { data: status } = useAccountStatus();
  const { data: subscription } = useSubscription();
  const { data: plansData } = usePlans();
  const [cycle, setCycle] = useState<Cycle>('quarterly');

  const isReconversionState =
    status?.access_state === 'blocked' ||
    status?.access_state === 'trial_expired' ||
    status?.access_state === 'subscription_suspended' ||
    status?.access_state === 'account_suspended';
  const isAllowed = isAllowedPath(location.pathname);
  const shouldShow = Boolean(status) && isReconversionState && !isAllowed;

  // Lock body scroll only while the screen is actually visible
  useEffect(() => {
    if (!shouldShow) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [shouldShow]);

  const plans = useMemo(() => {
    if (!plansData) return [] as Plan[];
    return plansData
      .filter((p) => p.name !== 'enterprise' && p.is_active)
      .sort((a, b) => a.price_monthly - b.price_monthly);
  }, [plansData]);

  const recommendedId = useMemo(() => {
    // Server-provided hint takes precedence (current plan, or "Pro" fallback).
    const fromServer = status?.recommended_plan_id;
    if (fromServer && plans.some((p) => p.id === fromServer)) return fromServer;
    const current = subscription?.subscription?.plan?.id;
    if (current && plans.some((p) => p.id === current)) return current;
    // Fallback: middle paid plan
    const paid = plans.filter((p) => p.price_monthly > 0);
    return paid[Math.floor(paid.length / 2)]?.id ?? paid[0]?.id ?? null;
  }, [plans, subscription, status]);

  if (!shouldShow || !status) return null;

  const usage = subscription?.usage;
  const firstName = user?.name?.split(' ')[0] ?? '';

  return (
    <div className="fixed inset-0 z-[55] overflow-y-auto bg-[#F7F7F8]">
      <header className="flex items-center justify-between border-b-[2.5px] border-charcoal-950 bg-white px-4 py-3 sm:px-8">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded bg-charcoal-950 font-heading text-sm font-extrabold text-lime-400">
            V
          </div>
          <span className="font-heading text-base font-bold sm:text-lg">Velko</span>
          <span className="hidden sm:inline-flex items-center gap-2 rounded border-2 border-charcoal-950 bg-charcoal-950 px-3 py-1 font-mono text-[11px] font-bold uppercase tracking-[0.06em] text-lime-400">
            <span className="h-2 w-2 rounded-full bg-lime-400" />
            {t('reconversion_status_readonly')}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to={ownerPath('/dashboard')}
            className="hidden text-sm font-semibold text-charcoal-500 underline underline-offset-[3px] hover:text-charcoal-950 sm:inline"
          >
            {t('reconversion_continue_readonly')}
          </Link>
          <Link
            to={ownerPath('/support')}
            className="text-sm font-semibold text-charcoal-500 underline underline-offset-[3px] hover:text-charcoal-950"
          >
            {t('reconversion_support_link')}
          </Link>
        </div>
      </header>

      <section className="mx-auto max-w-[920px] px-4 pt-12 pb-6 text-center sm:px-8 sm:pt-14">
        {firstName && (
          <span className="inline-flex items-center gap-2 rounded-md border-2 border-charcoal-950 bg-lime-100 px-3.5 py-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.08em] shadow-[2px_2px_0_#0d0d1a]">
            <span className="h-2 w-2 rounded-full bg-lime-500" />
            {t('reconversion_eyebrow', { name: firstName })}
          </span>
        )}
        <h1 className="mt-5 font-heading text-3xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl md:text-[56px]">
          {t('reconversion_h1_pre')}{' '}
          <em className="not-italic inline-block rounded-md border-[2.5px] border-charcoal-950 bg-lime-100 px-2 py-0.5 text-lime-700 shadow-[2px_2px_0_#0d0d1a] -rotate-1">
            {t('reconversion_h1_em')}
          </em>
          .
          <br />
          {t('reconversion_h1_after')}
        </h1>
        <p className="mx-auto mt-4 max-w-[660px] text-sm text-charcoal-600 sm:text-lg">
          {t('reconversion_lead')}
        </p>

        {usage && (
          <div className="mt-7 flex flex-wrap justify-center gap-2 sm:gap-4">
            <Stat icon={Store} value={usage.shops?.current ?? 0} label={t('shops')} />
            <Stat icon={Package} value={usage.products?.current ?? 0} label={t('products')} />
            <Stat icon={Users} value={usage.employees?.current ?? 0} label={t('employees')} />
            <Stat icon={Briefcase} value={usage.warehouses?.current ?? 0} label={t('warehouses')} />
          </div>
        )}
      </section>

      <section className="mx-auto max-w-[1280px] px-4 pb-12 sm:px-8">
        <div className="flex justify-center mb-6">
          <div className="inline-flex rounded-md border-[2.5px] border-charcoal-950 bg-white p-1 shadow-[2px_2px_0_#0d0d1a]">
            {(['monthly', 'quarterly', 'yearly'] as Cycle[]).map((c) => (
              <button
                key={c}
                onClick={() => setCycle(c)}
                className={cn(
                  'rounded-[4px] px-4 py-1.5 font-heading text-xs font-bold transition-colors sm:text-sm',
                  cycle === c
                    ? 'bg-charcoal-950 text-lime-400'
                    : 'text-charcoal-600 hover:bg-charcoal-50',
                )}
              >
                {t(`reconversion_cycle_${c}`)}
                {c !== 'monthly' && (
                  <span className="ml-1.5 rounded bg-lime-500 px-1.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider text-charcoal-950">
                    {c === 'quarterly' ? '−10%' : '−20%'}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-3 md:gap-5">
          {plans.map((plan) => {
            const isFeatured = plan.id === recommendedId;
            const { display, total, saving } = calcPriceForCycle(plan, cycle);
            const isFree = plan.price_monthly === 0;

            return (
              <article
                key={plan.id}
                className={cn(
                  'relative flex flex-col rounded-xl border-[2.5px] border-charcoal-950 px-6 py-7 sm:py-8',
                  isFeatured
                    ? 'bg-lime-50 shadow-[8px_8px_0_#0d0d1a] md:-translate-y-3'
                    : 'bg-white shadow-[3px_3px_0_#0d0d1a]',
                )}
              >
                {isFeatured && (
                  <span className="absolute -top-4 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 rounded-md border-[2.5px] border-charcoal-950 bg-lime-500 px-3.5 py-1 font-mono text-[11px] font-bold uppercase tracking-[0.08em] text-charcoal-950 shadow-[2px_2px_0_#0d0d1a] whitespace-nowrap">
                    <Star className="h-3 w-3 fill-current" />
                    {t('reconversion_recommended_badge')}
                  </span>
                )}

                <h3 className="font-heading text-xl font-extrabold sm:text-[22px]">
                  {plan.display_name}
                </h3>
                <p className="mt-1 mb-4 min-h-[38px] text-sm text-charcoal-600">
                  {plan.description}
                </p>

                <div className="flex items-end gap-1">
                  <span className="font-heading text-3xl font-extrabold leading-none tracking-tight sm:text-[44px]">
                    {isFree ? '0' : Math.round(display).toLocaleString('fr-FR')}
                  </span>
                  <small className="mb-1 font-sans text-sm font-medium text-charcoal-500">
                    {t('xof_per_month')}
                  </small>
                </div>
                {saving > 0 && (
                  <span className="mt-1 inline-block self-start rounded border-[1.5px] border-charcoal-950 bg-lime-500 px-2 py-0.5 font-mono text-[11px] font-bold uppercase tracking-wider text-charcoal-950">
                    {t('reconversion_saving_x', {
                      percent: saving,
                      total: formatCurrency(total, 'XOF'),
                    })}
                  </span>
                )}

                <ul className="mt-5 mb-6 flex-1 space-y-1.5">
                  <PlanLi
                    label={`${plan.max_shops ?? t('unlimited')} ${t('shops').toLowerCase()}`}
                    bold
                  />
                  <PlanLi
                    label={`${plan.max_products === null ? t('unlimited') : plan.max_products} ${t('products').toLowerCase()}`}
                  />
                  <PlanLi
                    label={`${plan.max_employees === null ? t('unlimited') : plan.max_employees} ${t('employees').toLowerCase()}`}
                  />
                  {plan.features.slice(0, 4).map((f) => (
                    <PlanLi key={f} label={t(`feature_${f}`, { defaultValue: f })} />
                  ))}
                </ul>

                <Link
                  to={ownerPath(`/subscription/checkout?plan=${plan.id}&cycle=${cycle}`)}
                  className={cn(
                    'inline-flex items-center justify-center rounded-md border-[2.5px] border-charcoal-950 px-5 py-3.5 font-heading text-sm font-bold transition-transform',
                    isFeatured
                      ? 'bg-lime-500 text-charcoal-950 shadow-[3px_3px_0_#0d0d1a] hover:-translate-x-px hover:-translate-y-px hover:shadow-[5px_5px_0_#0d0d1a]'
                      : 'bg-white text-charcoal-950 hover:bg-charcoal-50',
                  )}
                >
                  {isFeatured && saving > 0
                    ? t('reconversion_cta_resume_with_total', {
                        total: formatCurrency(total, 'XOF'),
                      })
                    : isFree
                      ? t('reconversion_cta_continue_free')
                      : t('reconversion_cta_choose', { plan: plan.display_name })}
                </Link>
              </article>
            );
          })}
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4 rounded-xl border-[2.5px] border-charcoal-950 bg-white p-6 shadow-[3px_3px_0_#0d0d1a] sm:grid-cols-2 md:grid-cols-4">
          <TrustItem
            icon={Phone}
            title={t('reconversion_trust_mobile_money_title')}
            sub={t('reconversion_trust_mobile_money_sub')}
          />
          <TrustItem
            icon={Phone}
            title={t('reconversion_trust_support_title')}
            sub={t('reconversion_trust_support_sub')}
          />
          <TrustItem
            icon={Clock}
            title={t('reconversion_trust_immediate_title')}
            sub={t('reconversion_trust_immediate_sub')}
          />
          <TrustItem
            icon={Users}
            title={t('reconversion_trust_users_title')}
            sub={t('reconversion_trust_users_sub')}
          />
        </div>

        <div className="mt-6 rounded-xl border-[2.5px] border-charcoal-950 bg-charcoal-950 p-6 text-center text-white shadow-[3px_3px_0_#0d0d1a]">
          <blockquote className="font-heading text-base font-semibold leading-snug sm:text-lg">
            <span className="text-lime-400">«&nbsp;</span>
            {t('reconversion_testimonial_quote')}
            <span className="text-lime-400">&nbsp;»</span>
          </blockquote>
          <div className="mt-3 font-mono text-[11px] font-bold uppercase tracking-[0.08em] text-lime-400">
            {t('reconversion_testimonial_author')}
          </div>
        </div>

        <nav className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 font-mono text-[11px] font-bold uppercase tracking-[0.08em] text-charcoal-500">
          <span>{t('reconversion_footer_data_safe')}</span>
          <span>•</span>
          <Link
            to={ownerPath('/subscription')}
            className="text-charcoal-700 underline underline-offset-[3px] hover:text-charcoal-950"
          >
            {t('reconversion_footer_cancel')}
          </Link>
          <span>•</span>
          <Link
            to={ownerPath('/support')}
            className="text-charcoal-700 underline underline-offset-[3px] hover:text-charcoal-950"
          >
            {t('reconversion_footer_support')}
          </Link>
          <span>•</span>
          <Link
            to={ownerPath('/dashboard')}
            className="text-charcoal-700 underline underline-offset-[3px] hover:text-charcoal-950"
          >
            {t('reconversion_continue_readonly')}
          </Link>
        </nav>
      </section>
    </div>
  );
}

function Stat({
  icon: Icon,
  value,
  label,
}: {
  icon: React.ComponentType<{ className?: string }>;
  value: number | string;
  label: string;
}) {
  return (
    <div className="flex items-center gap-2.5 rounded-md border-2 border-charcoal-950 bg-white px-3.5 py-2.5 shadow-[2px_2px_0_#0d0d1a] sm:px-4">
      <span className="flex h-8 w-8 items-center justify-center rounded bg-charcoal-950 text-lime-400">
        <Icon className="h-4 w-4" />
      </span>
      <div className="text-left">
        <div className="font-mono text-base font-bold leading-none sm:text-xl">{value}</div>
        <div className="text-xs font-medium text-charcoal-600">{label}</div>
      </div>
    </div>
  );
}

function PlanLi({ label, bold }: { label: React.ReactNode; bold?: boolean }) {
  return (
    <li className="flex items-start gap-2.5 text-sm leading-snug text-charcoal-700">
      <Check className="mt-1 h-3.5 w-3.5 shrink-0 text-lime-600" />
      <span className={cn(bold && 'font-bold text-charcoal-950')}>{label}</span>
    </li>
  );
}

function TrustItem({
  icon: Icon,
  title,
  sub,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  sub: string;
}) {
  return (
    <div className="flex items-center gap-3">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded border-2 border-charcoal-950 bg-lime-100">
        <Icon className="h-4.5 w-4.5 text-charcoal-950" />
      </span>
      <div>
        <b className="block font-heading text-sm font-bold">{title}</b>
        <span className="text-xs text-charcoal-600">{sub}</span>
      </div>
    </div>
  );
}
